import { useQuery } from '@tanstack/react-query';
import { useRequestSections } from '../../../api/request';
import { RootState } from '../../../redux/Store';
import { useSelector } from 'react-redux';
import { PersistHelper } from '../../../redux/persistHelper';
import customNotifications from './customNotifications';
import { useState } from 'react';
import { contextSupportTasksBids } from '../../../constants';

type GetLuggageTypeProps = {
  contextId?: string | number;
};

// Maps a ListTasks/ListBids instance to the engine's LuggageTask shape. The
// engine only keys on `${type}~${mode}`; category/media come from the task
// script during AddTaskByKey.
const toLuggageTask = (item: any) => ({
  id: String(item?.id),
  mode: item?.mode,
  type: item?.type,
});

const useGetLuggage = () => {
  // const queryClient = useQueryClient();

  const { Luggage, Tasks } = useRequestSections();
  const [isLuggageError, setIsLuggageError] = useState(false);

  const contextId = useSelector(
    (state: RootState) => state.contextId.contextId
  );
  const contextType = useSelector(
    (state: RootState) => state.contextId.queryObj?.context
  );
  // Contexts that show the Tasks/Bids tabs also pull their instances here.
  const supportsTasksBids = contextSupportTasksBids[contextType] ?? false;

  // Fetch script hash using the query
  const {
    data: luggageData,
    isLoading: isLuggageDataLoading,
    isFetching: isLuggagehDataFetching,
    refetch,
    error: luggageDataError,
  } = useQuery({
    queryKey: ['luggage', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted) {
        try {
          const response = await Luggage?.getLuggage(contextId);
          const luggage = response?.data ?? {};

          // Contexts that support Tasks/Bids pull the task and bid instances
          // and merge them into the luggage payload as `Tasks`/`Bids`. The
          // engine's LoadScript reads luggage.Tasks/.Bids and auto-adds each one
          // during load. Both only carry id/mode/type per the LuggageTask
          // contract.
          if (supportsTasksBids) {
            const [tasksResult, bidsResult] = await Promise.allSettled([
              Tasks?.listTasks(contextId, {}),
              Tasks?.listBids(contextId, {}),
            ]);

            if (tasksResult.status === 'fulfilled') {
              const tasks =
                tasksResult.value?.data?.tasks ??
                tasksResult.value?.data?.Tasks;
              if (Array.isArray(tasks)) {
                luggage.Tasks = tasks.map((task: any) => toLuggageTask(task));
              }
            } else {
              console.log('failed to list tasks', tasksResult.reason);
            }

            if (bidsResult.status === 'fulfilled') {
              const bids =
                bidsResult.value?.data?.bids ?? bidsResult.value?.data?.Bids;
              if (Array.isArray(bids)) {
                luggage.Bids = bids.map((bid: any) => toLuggageTask(bid));
              }
            } else {
              console.log('failed to list bids', bidsResult.reason);
            }
          }

          return JSON.stringify(luggage);
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Luggage data', errorCode);
          setIsLuggageError(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  return {
    luggageData,
    isLuggageDataLoading,
    isLuggagehDataFetching,
    refetch,
    luggageDataError,
    isLuggageError,
  };
};

export default useGetLuggage;
